/**
 * Workflow Automation Enums
 *
 * Enums for workflow rules, triggers, conditions, and actions.
 * Issue #313 - Centralize enums to shared-types
 */

import { z } from 'zod';

// ============================================================================
// TRIGGER TYPES
// ============================================================================

/**
 * Workflow trigger types
 * Defines when a workflow rule should be activated
 */
export enum WorkflowTriggerType {
  /** Triggered when a new record is created */
  RECORD_CREATED = 'record_created',
  /** Triggered when an existing record is updated */
  RECORD_UPDATED = 'record_updated',
  /** Triggered when a specific field value changes */
  FIELD_CHANGED = 'field_changed',
  /** Triggered when a record enters a pipeline stage */
  STAGE_ENTERED = 'stage_entered',
  /** Triggered when a record exits a pipeline stage */
  STAGE_EXITED = 'stage_exited',
  /** Triggered based on time/schedule */
  TIME_BASED = 'time_based',
  /** Triggered manually by user action */
  MANUAL = 'manual',
  /** Triggered via API call */
  API_TRIGGERED = 'api_triggered',
}

/** Zod schema for WorkflowTriggerType validation */
export const WorkflowTriggerTypeSchema = z.nativeEnum(WorkflowTriggerType);

/** Array of all trigger types for iteration */
export const WORKFLOW_TRIGGER_TYPES = Object.values(WorkflowTriggerType);

// ============================================================================
// TRIGGER ENTITIES
// ============================================================================

/**
 * Entities that can trigger workflows
 */
export enum WorkflowTriggerEntity {
  CASE = 'case',
  CONSUMER = 'consumer',
  TASK = 'task',
  DOCUMENT = 'document',
}

/** Zod schema for WorkflowTriggerEntity validation */
export const WorkflowTriggerEntitySchema = z.nativeEnum(WorkflowTriggerEntity);

/** Array of all trigger entities for iteration */
export const WORKFLOW_TRIGGER_ENTITIES = Object.values(WorkflowTriggerEntity);

// ============================================================================
// WORKFLOW CONDITION OPERATORS
// ============================================================================

/**
 * Operators for workflow condition evaluation
 * Note: Distinguished from ConditionOperator in entity-membership.enums.ts
 */
export enum WorkflowConditionOperator {
  EQUALS = 'equals',
  NOT_EQUALS = 'not_equals',
  CONTAINS = 'contains',
  NOT_CONTAINS = 'not_contains',
  STARTS_WITH = 'starts_with',
  ENDS_WITH = 'ends_with',
  GREATER_THAN = 'greater_than',
  LESS_THAN = 'less_than',
  GREATER_OR_EQUAL = 'greater_or_equal',
  LESS_OR_EQUAL = 'less_or_equal',
  IS_EMPTY = 'is_empty',
  IS_NOT_EMPTY = 'is_not_empty',
  IN_LIST = 'in_list',
  NOT_IN_LIST = 'not_in_list',
}

/** Zod schema for WorkflowConditionOperator validation */
export const WorkflowConditionOperatorSchema = z.nativeEnum(WorkflowConditionOperator);

/** Array of all workflow condition operators for iteration */
export const WORKFLOW_CONDITION_OPERATORS = Object.values(WorkflowConditionOperator);

/**
 * Logical operators for combining conditions
 */
export enum LogicalOperator {
  AND = 'AND',
  OR = 'OR',
}

/** Zod schema for LogicalOperator validation */
export const LogicalOperatorSchema = z.nativeEnum(LogicalOperator);

// ============================================================================
// ACTION TYPES
// ============================================================================

/**
 * Workflow action types
 * Defines what actions can be performed when a workflow triggers
 */
export enum WorkflowActionType {
  /** Send an email notification */
  SEND_EMAIL = 'send_email',
  /** Send an SMS message */
  SEND_SMS = 'send_sms',
  /** Update a field value on the record */
  UPDATE_FIELD = 'update_field',
  /** Create a new task */
  CREATE_TASK = 'create_task',
  /** Assign the record to a user */
  ASSIGN_USER = 'assign_user',
  /** Move the record to a different pipeline stage */
  MOVE_STAGE = 'move_stage',
  /** Add a tag to the record */
  ADD_TAG = 'add_tag',
  /** Remove a tag from the record */
  REMOVE_TAG = 'remove_tag',
  /** Call an external webhook */
  WEBHOOK = 'webhook',
  /** Delay before next action */
  DELAY = 'delay',
  /** Send in-app notification to user */
  NOTIFY_USER = 'notify_user',
  /** Create a new case */
  CREATE_CASE = 'create_case',
  /** Update case status */
  UPDATE_CASE_STATUS = 'update_case_status',
}

/** Zod schema for WorkflowActionType validation */
export const WorkflowActionTypeSchema = z.nativeEnum(WorkflowActionType);

/** Array of all action types for iteration */
export const WORKFLOW_ACTION_TYPES = Object.values(WorkflowActionType);

// ============================================================================
// WORKFLOW RULE STATUS
// ============================================================================

/**
 * Workflow rule status
 */
export enum WorkflowRuleStatus {
  ACTIVE = 'active',
  INACTIVE = 'inactive',
  DRAFT = 'draft',
}

/** Zod schema for WorkflowRuleStatus validation */
export const WorkflowRuleStatusSchema = z.nativeEnum(WorkflowRuleStatus);
